/**
 * Direct client-side lookups against keys.openpgp.org.
 * Used to avoid funneling all document validation lookups through the worker proxy,
 * which would hit the global 5/sec rate limit fast.
 */

const REGISTRY_URL = "https://keys.openpgp.org";

/**
 * Look up a public key directly by Key ID (16 chars).
 * Falls back to the worker proxy if CORS or network fails.
 */
export async function lookupByKeyId(keyId: string): Promise<string | null> {
  try {
    const resp = await fetch(
      `${REGISTRY_URL}/vks/v1/by-keyid/${keyId.toUpperCase()}`,
    );
    if (resp.ok) return await resp.text();
    if (resp.status === 404) return null;
  } catch (e) {
    // CORS blocked or network error — fall through to worker
    console.warn(
      "Direct keyserver lookup failed, falling back to worker proxy:",
      e,
    );
  }

  // Fallback to worker proxy (shared rate limit, but KV-cached)
  try {
    const resp = await fetch("/api/keyserver/lookup-keyid", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keyid: keyId }),
    });
    if (resp.ok) return await resp.text();
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Look up a public key directly by Email.
 * Falls back to the worker proxy if CORS or network fails.
 */
export async function lookupByEmail(email: string): Promise<string | null> {
  try {
    const resp = await fetch(
      `${REGISTRY_URL}/vks/v1/by-email/${encodeURIComponent(email)}`,
    );
    if (resp.ok) return await resp.text();
    if (resp.status === 404) return null;
  } catch (e) {
    // CORS blocked or network error — fall through to worker
    console.warn(
      "Direct keyserver lookup failed, falling back to worker proxy:",
      e,
    );
  }

  // Fallback to worker proxy
  try {
    const resp = await fetch("/api/keyserver/lookup-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (resp.ok) return await resp.text();
    return null;
  } catch (e) {
    console.error("Worker keyserver proxy failed:", e);
    return null;
  }
}

/**
 * Publish a public key to the registry.
 * Always goes through the worker.
 */
export async function uploadKey(
  publicKeyArmored: string,
): Promise<{ token: string; fingerprint: string }> {
  const resp = await fetch("/api/keyserver/upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ keytext: publicKeyArmored }),
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    throw new Error(`Failed to upload key: ${resp.status} - ${errorText}`);
  }

  const data = await resp.json();
  if (data.error) {
    throw new Error(data.error);
  }
  return { token: data.token, fingerprint: data.key_fpr };
}

/**
 * Request an email verification link after publishing.
 * Always goes through the worker.
 */
export async function requestVerification(
  token: string,
  email: string,
): Promise<boolean> {
  const resp = await fetch("/api/keyserver/request-verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, addresses: [email], locale: ["en"] }),
  });

  return resp.ok;
}
